<template>
    <div>
        <div class="col-12">
            <div class="text-h4 text-weight-medium">
                {{ $t('Shift Management') }}
            </div>
            <div class="page-description">
                {{
                    $t('Configure work shifts for {facilityName}.', {
                        facilityName: this.facility?.name
                    })
                }}
            </div>
        </div>
        <q-table
            :loading="isLoadingShifts"
            :rows="shifts"
            :columns="columns"
            v-model:pagination="pagination"
            :rows-per-page-options="[pagination.rowsPerPage]"
            :hide-bottom="shifts.length > 0"
            :no-data-label="
                $t(
                    'This facility has no shifts yet. Click on Add Shift to add a shift.'
                )
            "
        >
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-accent text-white"
                    >
                        {{ $t(col.label) }}
                    </q-th>
                </q-tr>
            </template>
            <!-- Name and time range columns -->
            <template v-slot:body-cell-name="props">
                <q-td :props="props">
                    <q-input
                        class="bg-white q-mt-md shift-name-input"
                        v-if="props.row.isEdit"
                        :model-value="props.row.name"
                        @update:model-value="
                            val => updateShiftField(props.row, 'name', val)
                        "
                        :label="$t('Name')"
                        :rules="[val => val !== '' || $t('Enter a name')]"
                        :hint="$t('Name of the shift')"
                        type="text"
                        outlined
                        dense
                        color="accent"
                    />
                    <div v-else>{{ props.row.name }}</div>
                </q-td>
            </template>
            <template v-slot:body-cell-start="props">
                <q-td :props="props">
                    <q-input
                        v-if="props.row.isEdit"
                        :model-value="props.row.start_hour"
                        @update:model-value="
                            val =>
                                updateShiftField(props.row, 'start_hour', val)
                        "
                        @click="handleShowTimePicker(props.row, 'start_hour')"
                        :label="$t('Start')"
                        :hint="$t('Start time of the shift')"
                        mask="##:##"
                        class="q-mt-md shift-start-input"
                        outlined
                        dense
                        color="accent"
                    />
                    <div v-else>
                        {{ formatTime(props.row.start_hour) }}
                    </div>
                </q-td>
            </template>
            <template v-slot:body-cell-end="props">
                <q-td :props="props">
                    <q-input
                        v-if="props.row.isEdit"
                        :model-value="props.row.end_hour"
                        @update:model-value="
                            val => updateShiftField(props.row, 'end_hour', val)
                        "
                        @click="handleShowTimePicker(props.row, 'end_hour')"
                        :label="$t('End')"
                        :hint="$t('End time of the shift')"
                        mask="##:##"
                        class="q-mt-md shift-end-input"
                        outlined
                        dense
                        color="accent"
                    />
                    <div v-else>
                        {{ formatTime(props.row.end_hour) }}
                    </div>
                </q-td>
            </template>

            <!-- Day columns -->
            <template v-slot:body-cell-sunday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.sunday"
                        @update:model-value="
                            val => updateDayField(props.row, 'sunday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-monday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.monday"
                        @update:model-value="
                            val => updateDayField(props.row, 'monday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-tuesday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.tuesday"
                        @update:model-value="
                            val => updateDayField(props.row, 'tuesday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-wednesday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.wednesday"
                        @update:model-value="
                            val => updateDayField(props.row, 'wednesday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-thursday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.thursday"
                        @update:model-value="
                            val => updateDayField(props.row, 'thursday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-friday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.friday"
                        @update:model-value="
                            val => updateDayField(props.row, 'friday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>
            <template v-slot:body-cell-saturday="props">
                <q-td :props="props">
                    <q-checkbox
                        :model-value="props.row.saturday"
                        @update:model-value="
                            val => updateDayField(props.row, 'saturday', val)
                        "
                        :disable="!props.row.isEdit"
                        :color="props.row.isEdit ? 'accent' : 'grey'"
                    />
                </q-td>
            </template>

            <!-- Action column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md justify-center">
                        <q-btn
                            v-if="props.row.isEdit"
                            @click="save(props.row)"
                            :disable="
                                $userCannot('create', 'shifts') ||
                                $userCannot('edit', 'shifts')
                            "
                            class="table__action-button"
                            color="positive"
                            icon="fas fa-check"
                            outline
                        >
                            <q-tooltip>{{ $t('Save shift') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="props.row.isEdit"
                            @click="cancelEdit(props.row)"
                            class="table__action-button"
                            icon="fas fa-times"
                            color="negative"
                            outline
                        >
                            <q-tooltip>{{ $t('Cancel edit') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="!props.row.isEdit"
                            :disable="$userCannot('edit', 'shifts')"
                            :color="
                                $userCannot('edit', 'shifts')
                                    ? 'grey'
                                    : 'accent'
                            "
                            @click="edit(props.row)"
                            class="table__action-button"
                            icon="fas fa-edit"
                            outline
                        >
                            <q-tooltip v-if="!props.row.isEdit">
                                {{
                                    $userCannot('edit', 'shifts')
                                        ? $t(
                                              'You do not have permission to perform this action.'
                                          )
                                        : $t('Edit shift')
                                }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            v-if="props.row.isEdit"
                            :disable="$userCannot('delete', 'shifts')"
                            @click="handleRemove(props.row)"
                            class="table__action-button"
                            color="negative"
                            icon="fas fa-trash-alt"
                        >
                            <q-tooltip>{{ $t('Delete shift') }}</q-tooltip>
                        </q-btn>
                    </div>
                </q-td>
            </template>
        </q-table>

        <div align="right" class="q-mt-md">
            <q-btn
                @click="addShift"
                :label="$t('Add shift')"
                :disable="$userCannot('create', 'shifts') || this.isAdding"
                :color="$userCannot('create', 'shifts') ? 'grey' : 'accent'"
                class="q-mb-md"
            />
        </div>

        <q-dialog v-model="timePicker.show">
            <q-card>
                <time-picker
                    v-model="timePicker.value"
                    :minute-interval="'15'"
                />
                <div class="row q-ma-md">
                    <div align="right" class="col">
                        <q-btn
                            @click="timePicker.show = false"
                            :label="$t('Cancel')"
                            class="q-mr-sm"
                            color="white"
                            text-color="primary"
                            outline
                        />
                        <q-btn
                            @click="handleTimePickerSelect"
                            :label="$t('Select')"
                            color="primary"
                        />
                    </div>
                </div>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import TimePicker from '../components/TimePicker.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { sanitizeString } from '@utils/sanitize.js';

export default {
    name: 'ShiftList',
    components: {
        TimePicker
    },
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // get shifts
        this.getShifts();
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Shift Management')
            }
        ]);
    },
    data() {
        return {
            columns: [
                {
                    name: 'name',
                    field: 'name',
                    label: 'Name',
                    align: 'left',
                    headerStyle: 'min-width: 150px;'
                },
                {
                    name: 'start',
                    field: 'start',
                    label: 'Start',
                    align: 'left',
                    headerStyle: 'min-width: 80px;'
                },
                {
                    name: 'end',
                    field: 'end',
                    label: 'End',
                    align: 'left',
                    headerStyle: 'min-width: 80px;'
                },
                {
                    name: 'sunday',
                    label: 'Sun',
                    align: 'center',
                    required: true
                },
                {
                    name: 'monday',
                    label: 'Mon',
                    align: 'center',
                    required: true
                },
                {
                    name: 'tuesday',
                    label: 'Tue',
                    align: 'center',
                    required: true
                },
                {
                    name: 'wednesday',
                    label: 'Wed',
                    align: 'center',
                    required: true
                },
                {
                    name: 'thursday',
                    label: 'Thu',
                    align: 'center',
                    required: true
                },
                {
                    name: 'friday',
                    label: 'Fri',
                    align: 'center',
                    required: true
                },
                {
                    name: 'saturday',
                    label: 'Sat',
                    align: 'center',
                    required: true
                },
                {
                    name: 'actions',
                    label: 'Actions',
                    align: 'center',
                    required: true
                }
            ],

            shiftEditStates: new Map(), // Map<shiftId|tempId, editState>
            shiftBackupData: new Map(), // Map<shiftId|tempId, backupData>
            tempShiftCounter: 0,
            isLoadingShifts: false,
            timePicker: {
                show: false,
                row: null,
                key: null,
                value: null
            },

            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility', 'getShiftsByFacility']),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale',
            user: 'user'
        }),
        /**
         * Get shifts from store with UI state added.
         * Single source of truth from Pinia store.
         * @returns {Array} Array of shift objects with UI properties.
         */
        shifts() {
            const storeShifts = this.getShiftsByFacility(this.facilityId) || [];
            return storeShifts.map(shift => {
                const shiftId = shift.id || `temp_${shift.tempId || 0}`;
                const editState = this.shiftEditStates.get(shiftId) || {};
                return {
                    ...shift,
                    rowId: shiftId,
                    isEdit: editState.isEdit || false,
                    error: editState.error || false
                };
            });
        },
        /**
         * Checks if there is any shift in the list that is currently being added.
         * A shift is considered to be in the process of being added if its `id` is `null`.
         * @returns {boolean} - Returns `true` if there is at least one shift with `id` as `null`, otherwise `false`.
         */
        isAdding() {
            return this.shifts.some(shift => shift.id === null);
        },
        /**
         * Retrieves the facility information based on the current facility ID.
         * @returns {Object} The facility information.
         */
        facility() {
            return this.getFacility(this.facilityId);
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFacilitiesStore, [
            'loadShiftsAction',
            'addShiftToStoreAction',
            'updateShiftInStoreAction',
            'removeShiftFromStoreAction'
        ]),
        /**
         * Format time in hh:mm:ss to hh:mm
         * @return {String}
         */
        formatTime(time) {
            const [hour, minute, second] = time.split(':');
            return `${hour}:${minute}`;
        },
        /**
         * Generate a temporary ID for new shifts.
         * @return {Number}
         */
        generateTempId() {
            return ++this.tempShiftCounter;
        },
        /**
         * Find a shift in the store by row identifier.
         * @param {Object} row - The shift row.
         * @returns {Object|null} The shift object from store or null if not found.
         */
        findShiftInStore(row) {
            const storeShifts = this.getShiftsByFacility(this.facilityId);
            if (!storeShifts) return null;

            return storeShifts.find(s => {
                if (row.id) {
                    return s.id === row.id;
                } else {
                    return s.tempId === row.tempId;
                }
            });
        },
        /**
         * Update any field for a shift in the store.
         * Optimized to minimize store lookups.
         * @param {Object} row - The shift row.
         * @param {String} field - The field name (e.g., 'name', 'start_hour', 'monday').
         * @param {Any} value - The new value.
         */
        updateShiftField(row, field, value) {
            const shift = this.findShiftInStore(row);
            if (shift) {
                const updatedShift = {
                    ...shift,
                    [field]: value
                };
                this.updateShiftInStoreAction(this.facilityId, updatedShift);
            }
        },
        /**
         * Update a day field (checkbox) for a shift in the store.
         * @param {Object} row - The shift row.
         * @param {String} dayField - The day field name (e.g., 'monday', 'tuesday').
         * @param {Boolean} value - The new value.
         */
        updateDayField(row, dayField, value) {
            this.updateShiftField(row, dayField, value);
        },
        /**
         * Add a shift in the table.
         */
        addShift() {
            // check if all table rows are saved
            const isAllRowsSaved = this.shifts.every(
                shift => shift.id !== null
            );

            // only allow a single create row
            if (isAllRowsSaved) {
                const tempId = this.generateTempId();
                // set shift values
                const shift = {
                    id: null,
                    tempId: tempId,
                    name: '',
                    start_hour: '00:00:00',
                    end_hour: '12:00:00',
                    sunday: true,
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    facility: this.facilityId
                };

                // Add to store
                this.addShiftToStoreAction(this.facilityId, shift);

                // Mark as editing
                this.shiftEditStates.set(`temp_${tempId}`, {
                    isEdit: true,
                    error: false
                });
            }
        },

        /**
         * Get shifts from the store or server.
         * First checks if shifts exist in the store, otherwise fetches from API.
         */
        async getShifts() {
            // Check if shifts already exist in the store
            const shiftsData = this.getShiftsByFacility(this.facilityId);

            // If shifts haven't been fetched yet (null), fetch from API
            if (shiftsData === null) {
                this.isLoadingShifts = true;
                try {
                    await this.loadShiftsAction(this.facilityId);
                } catch (error) {
                    console.error('error: ', error);
                    this.$notifyError(
                        this.$t(
                            'Failed to fetch shifts, please try again later.'
                        ),
                        error
                    );
                    return;
                } finally {
                    this.isLoadingShifts = false;
                }
            }
        },
        /**
         * Update shift.
         * @param {Object} shift
         */
        async update(shift, row) {
            this.beginLoading(`${this.$t('Updating shift')}...`);

            try {
                const response = await this.$axios.put(
                    `/facilities/${this.facilityId}/shifts/${shift.id}/`,
                    shift
                );

                // Optimistic update: Update store directly with response
                const updatedShift = response.data;
                this.updateShiftInStoreAction(this.facilityId, updatedShift);

                // Update edit state
                this.shiftEditStates.set(shift.id, {
                    isEdit: false,
                    error: false
                });

                this.$notifySuccess(this.$t('Shift updated'));

                // Ensure reactivity completes
                await this.$nextTick();
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed to update shift, please try again later.'),
                    error
                );
                // Keep shift as editable
                this.shiftEditStates.set(shift.id, {
                    isEdit: true,
                    error: true
                });
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Create shift.
         * @param {Object} shift
         */
        async create(shift, row) {
            this.beginLoading(`${this.$t('Creating shift')}...`);

            const tempId = `temp_${shift.tempId}`;

            try {
                const response = await this.$axios.post(
                    `/facilities/${this.facilityId}/shifts/`,
                    shift
                );

                // Remove temp shift and add real shift from response
                const newShift = response.data;
                this.removeShiftFromStoreAction(
                    this.facilityId,
                    shift.tempId,
                    true
                );
                this.addShiftToStoreAction(this.facilityId, newShift);

                // Clean up temp editing state
                this.shiftEditStates.delete(tempId);
                this.shiftBackupData.delete(tempId);

                this.$notifySuccess(this.$t('Shift created'));

                // Ensure reactivity completes
                await this.$nextTick();
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed to create shift, please try again later.'),
                    error
                );
                // Keep shift as editable
                this.shiftEditStates.set(tempId, {
                    isEdit: true,
                    error: true
                });
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Delete shift.
         * @param {Number} shiftId
         */
        async remove(shiftId) {
            this.beginLoading(`${this.$t('Deleting shift')}...`);

            try {
                await this.$axios.delete(
                    `/facilities/${this.facilityId}/shifts/${shiftId}`
                );

                // Optimistic update: Remove from store
                this.removeShiftFromStoreAction(this.facilityId, shiftId);

                // Clean up editing state
                this.shiftEditStates.delete(shiftId);
                this.shiftBackupData.delete(shiftId);

                this.$notifySuccess(this.$t('Shift deleted.'));

                // Ensure reactivity completes
                await this.$nextTick();
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed to delete shift, please try again later.'),
                    error
                );
                // On error, reload from server to restore correct state
                await this.loadShiftsAction(this.facilityId);
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handle row edit.
         * Keep track of the fields before editing
         * @param {Object} row - Row data.
         */
        edit(row) {
            const shiftId = row.id || `temp_${row.tempId}`;

            // Save a backup of the shift data
            const backup = {};
            for (const property in row) {
                if (
                    property !== 'rowId' &&
                    property !== 'isEdit' &&
                    property !== 'error'
                ) {
                    backup[property] = JSON.parse(
                        JSON.stringify(row[property])
                    );
                }
            }
            this.shiftBackupData.set(shiftId, backup);

            // Mark as editing
            this.shiftEditStates.set(shiftId, {
                isEdit: true,
                error: false
            });
        },
        /**
         * Handle row cancel edit.
         * Restore backup data or remove temp shift.
         * @param {Object} row - Row data.
         */
        cancelEdit(row) {
            const shiftId = row.id || `temp_${row.tempId}`;

            // If it's a new shift (no id), remove it from store
            if (!row.id) {
                this.removeShiftFromStoreAction(
                    this.facilityId,
                    row.tempId,
                    true
                );
                this.shiftEditStates.delete(shiftId);
                this.shiftBackupData.delete(shiftId);
                return;
            }

            // Restore from backup for existing shifts
            const backup = this.shiftBackupData.get(shiftId);
            if (backup) {
                this.updateShiftInStoreAction(this.facilityId, backup);
            }

            // Close editing mode
            this.shiftEditStates.set(shiftId, {
                isEdit: false,
                error: false
            });
            this.shiftBackupData.delete(shiftId);
        },
        /**
         * Build shift payload from a table row
         * @param {Object} row
         * @return {Object} shift data
         */
        getShiftData(row) {
            return {
                id: row.id,
                tempId: row.tempId, // Include tempId for temp shift identification
                name: row.name,
                start_hour: row.start_hour,
                end_hour: row.end_hour,
                facility: row.facility,
                sunday: row.sunday,
                monday: row.monday,
                tuesday: row.tuesday,
                wednesday: row.wednesday,
                thursday: row.thursday,
                friday: row.friday,
                saturday: row.saturday
            };
        },
        /**
         * Handle row save.
         * @param {Object} row - Row data.
         */
        async save(row) {
            // get shift data (drop frontend fields)
            const shift = this.getShiftData(row);

            // Sanitize and normalize data before validation
            this.sanitizeShiftData(shift);

            // Update the shift in store with sanitized values so UI reflects them
            const storeShift = this.findShiftInStore(row);
            if (storeShift) {
                this.updateShiftInStoreAction(this.facilityId, {
                    ...storeShift,
                    name: shift.name,
                    start_hour: shift.start_hour,
                    end_hour: shift.end_hour
                });
            }

            // validate payload
            const isValid = this.validate(shift);
            if (isValid) {
                if (shift.id) {
                    await this.update(shift, row);
                } else {
                    await this.create(shift, row);
                }
            }
        },
        /**
         * Sanitize and normalize shift data (mutates the shift object).
         * @param {Object} shift - The shift object to sanitize.
         */
        sanitizeShiftData(shift) {
            // Sanitize and trim name
            if (shift.name) {
                shift.name = shift.name.trim();
                shift.name = sanitizeString(shift.name);
            }

            // Normalize time format - add seconds if not present (HH:MM -> HH:MM:SS)
            // The analog clock removes seconds when selecting time, so we need to add them back
            if (
                shift.start_hour &&
                /^([01]\d|2[0-3]):([0-5]\d)$/.test(shift.start_hour)
            ) {
                shift.start_hour = `${shift.start_hour}:00`;
            }
            if (
                shift.end_hour &&
                /^([01]\d|2[0-3]):([0-5]\d)$/.test(shift.end_hour)
            ) {
                shift.end_hour = `${shift.end_hour}:00`;
            }
        },
        /**
         * Validate shift.
         * Assumes shift data has already been sanitized via sanitizeShiftData().
         * @param {Object} shift
         * @return {Boolean} True if shift is valid
         */
        validate(shift) {
            let isValid = true;

            // Validate name
            if (!shift.name || shift.name === '') {
                this.$notifyError(this.$t('A shift name must be provided.'));
                isValid = false;
                return isValid;
            }

            // Validate name length (reasonable limit)
            if (shift.name.length > 40) {
                this.$notifyError(
                    this.$t('Shift name must be 40 characters or less.')
                );
                isValid = false;
                return isValid;
            }

            // Validate time format (HH:MM:SS)
            const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
            if (!timeRegex.test(shift.start_hour)) {
                this.$notifyError(
                    this.$t('Invalid start time format. Use HH:MM format.')
                );
                isValid = false;
                return isValid;
            }
            if (!timeRegex.test(shift.end_hour)) {
                this.$notifyError(
                    this.$t('Invalid end time format. Use HH:MM format.')
                );
                isValid = false;
                return isValid;
            }

            // Must check at least one day
            const days = [
                shift.sunday,
                shift.monday,
                shift.tuesday,
                shift.wednesday,
                shift.thursday,
                shift.friday,
                shift.saturday
            ];
            if (days.every(day => day === false)) {
                this.$notifyError(
                    this.$t('At least one day of the week must be selected.')
                );
                isValid = false;
                return isValid;
            }

            return isValid;
        },
        /**
         * Handle remove shift.
         * @param {Object} row
         */
        async handleRemove(row) {
            await this.$confirmDelete(
                this.$t('Are you sure you want to delete this shift?')
            ).onOk(async () => {
                if (row.id) {
                    // Delete saved shift from server
                    await this.remove(row.id);
                } else {
                    // Remove unsaved temp shift from store
                    this.removeShiftFromStoreAction(
                        this.facilityId,
                        row.tempId,
                        true
                    );
                    const tempId = `temp_${row.tempId}`;
                    this.shiftEditStates.delete(tempId);
                    this.shiftBackupData.delete(tempId);
                }
            });
        },
        /**
         * Handle show time picker. Sets the active row for the time picker and the key and value to use when confirmed.
         * @param {String} row - Active row to edit.
         * @param {String} key - Key name to edit (i.e. 'start_hour' or 'end_hour')
         */
        handleShowTimePicker(row, key) {
            this.timePicker.show = true;
            this.timePicker.row = row;
            this.timePicker.key = key;
            // if the row already has a time set, use it
            const time = this.timePicker.row[this.timePicker.key];

            if (time) {
                this.timePicker.value = time;
            }
        },
        /**
         * Set the selected time to the row.
         */
        handleTimePickerSelect() {
            // Update through the store to maintain single source of truth
            this.updateShiftField(
                this.timePicker.row,
                this.timePicker.key,
                this.timePicker.value
            );
            this.timePicker.show = false;
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.shift-header) {
    font-size: 17px;
}

:deep(.shift-cell) {
    font-weight: 700;
}

:deep(.action-header) {
    font-size: 17px;
}
</style>
